/**
 * useGlobalSoftphone.js
 *
 * Global SIP registration hook that lives in Layout.
 * Keeps the RingCentral WebPhone SDK registered so incoming calls
 * ring on all pages (not just the Phone tab).
 *
 * On Android (VirgoAndroid bridge):
 * - Starts foreground service when SIP registers (keeps process alive)
 * - Fires full-screen incoming call notification with ringtone
 * - Handles answer/decline from Android notification or lock screen
 * - Stops service when SIP unregisters
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { ringCentralService } from '../services/ringCentralService';
import api from '../services/api';

// Module-level reference to the pending incoming SIP session.
// Used by answer/decline event handlers to accept or reject the call.
let pendingIncomingSession = null;

export const useGlobalSoftphone = ({ onIncomingSipCall, onSipCallEnded } = {}) => {
  const [sipState, setSipState] = useState('disconnected');
  const initRef = useRef(false);
  const retryTimerRef = useRef(null);
  const onIncomingSipCallRef = useRef(onIncomingSipCall);
  const onSipCallEndedRef = useRef(onSipCallEnded);

  // Keep callback refs fresh
  useEffect(() => {
    onIncomingSipCallRef.current = onIncomingSipCall;
    onSipCallEndedRef.current = onSipCallEnded;
  }, [onIncomingSipCall, onSipCallEnded]);

  const initializeSip = useCallback(async () => {
    // Skip if already registered or currently initializing
    if (ringCentralService.getIsRegistered() || initRef.current) {
      if (ringCentralService.getIsRegistered()) {
        setSipState('registered');
      }
      return;
    }

    initRef.current = true;

    try {
      // Set up global callbacks (these persist even when Softphone unmounts)
      ringCentralService.on('onConnectionStateChange', (state) => {
        setSipState(state);
      });

      ringCentralService.on('onRegistered', () => {
        setSipState('registered');
        console.log('[GlobalSoftphone] SIP registered — phone is online');

        // Tell Android to start the foreground service (keeps SIP alive in background)
        if (window.VirgoAndroid && window.VirgoAndroid.onSipRegistered) {
          window.VirgoAndroid.onSipRegistered();
        }
      });

      ringCentralService.on('onRegistrationFailed', (err) => {
        setSipState('error');
        console.warn('[GlobalSoftphone] SIP registration failed:', err);

        // Tell Android to stop the foreground service
        if (window.VirgoAndroid && window.VirgoAndroid.onSipUnregistered) {
          window.VirgoAndroid.onSipUnregistered();
        }

        // Retry after 30 seconds
        scheduleRetry(30000);
      });

      ringCentralService.on('onUnregistered', () => {
        setSipState('disconnected');
        console.warn('[GlobalSoftphone] SIP unregistered — scheduling reconnect');

        // Tell Android to stop the foreground service
        if (window.VirgoAndroid && window.VirgoAndroid.onSipUnregistered) {
          window.VirgoAndroid.onSipUnregistered();
        }

        scheduleRetry(10000);
      });

      ringCentralService.on('onIncomingCall', (callInfo, session) => {
        console.log('[GlobalSoftphone] Incoming SIP call:', callInfo);

        // Store the session so answer/decline events can use it
        pendingIncomingSession = session;

        const callerName = callInfo?.fromName || callInfo?.from || 'Unknown';
        const callerNumber = callInfo?.from || '';

        // Trigger Android full-screen incoming call notification
        if (window.VirgoAndroid && window.VirgoAndroid.showIncomingCall) {
          window.VirgoAndroid.showIncomingCall(callerName, callerNumber);
        } else if (window.VirgoAndroid && window.VirgoAndroid.showNotification) {
          // Fallback for older bridge without showIncomingCall
          window.VirgoAndroid.showNotification(
            'Incoming Call',
            'Call from ' + callerName
          );
        }

        // Vibrate on Android (belt and suspenders with the notification)
        if (window.VirgoAndroid && window.VirgoAndroid.vibrate) {
          window.VirgoAndroid.vibrate(1000);
        }

        // Forward to Layout immediately (shows banner while screen pop loads)
        onIncomingSipCallRef.current?.(callInfo, session);

        // Fetch screen pop data from Virgo backend and update the call
        if (callerNumber) {
          api.get('/telnyx/screen-pop', { params: { phone: callerNumber } })
            .then(res => {
              if (res.data && Object.keys(res.data).length > 0) {
                console.log('[GlobalSoftphone] Screen pop data received:', res.data);
                const enrichedCallInfo = {
                  ...callInfo,
                  screen_pop: res.data,
                };
                onIncomingSipCallRef.current?.(enrichedCallInfo, session);
              }
            })
            .catch(err => console.warn('[GlobalSoftphone] Screen pop fetch failed:', err.message));
        }
      });

      ringCentralService.on('onCallEnded', (session) => {
        console.log('[GlobalSoftphone] Call ended');
        pendingIncomingSession = null;

        // Dismiss Android incoming call notification
        if (window.VirgoAndroid && window.VirgoAndroid.dismissIncomingCall) {
          window.VirgoAndroid.dismissIncomingCall();
        }

        onSipCallEndedRef.current?.(session);
      });

      ringCentralService.on('onCallFailed', () => {
        console.log('[GlobalSoftphone] Call failed');
        pendingIncomingSession = null;

        // Dismiss Android incoming call notification
        if (window.VirgoAndroid && window.VirgoAndroid.dismissIncomingCall) {
          window.VirgoAndroid.dismissIncomingCall();
        }

        onSipCallEndedRef.current?.();
      });

      await ringCentralService.initialize();
      console.log('[GlobalSoftphone] SIP initialized successfully');
    } catch (err) {
      console.warn('[GlobalSoftphone] SIP init failed:', err.message);
      setSipState('error');
      // Retry — RC might not be configured yet, or token expired
      scheduleRetry(60000);
    } finally {
      initRef.current = false;
    }
  }, []);

  const scheduleRetry = useCallback((delay) => {
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => {
      console.log('[GlobalSoftphone] Retrying SIP registration...');
      initRef.current = false;
      initializeSip();
    }, delay);
  }, [initializeSip]);

  // Listen for answer/decline events from Android (dispatched by VirgoActivity
  // when user taps Answer/Decline on the incoming call notification or lock screen)
  useEffect(() => {
    const handleAnswer = () => {
      console.log('[GlobalSoftphone] Answer event received from Android');
      if (pendingIncomingSession) {
        try {
          // Accept the SIP session — audio will be handled by WebRTC
          pendingIncomingSession.accept();
          console.log('[GlobalSoftphone] Auto-answered incoming call');
        } catch (e) {
          console.error('[GlobalSoftphone] Auto-answer failed:', e);
          // Even if auto-accept fails, user is navigated to /voip where
          // they can manually answer via the Softphone UI
        }
        pendingIncomingSession = null;
      }
    };

    const handleDecline = () => {
      console.log('[GlobalSoftphone] Decline event received from Android');
      if (pendingIncomingSession) {
        try {
          pendingIncomingSession.reject();
          console.log('[GlobalSoftphone] Declined incoming call');
        } catch (e) {
          console.error('[GlobalSoftphone] Decline failed:', e);
          try {
            pendingIncomingSession.terminate();
          } catch (e2) {
            // Last resort
          }
        }
        pendingIncomingSession = null;

        // Dismiss Android notification
        if (window.VirgoAndroid && window.VirgoAndroid.dismissIncomingCall) {
          window.VirgoAndroid.dismissIncomingCall();
        }
      }
    };

    window.addEventListener('virgo-answer-call', handleAnswer);
    window.addEventListener('virgo-decline-call', handleDecline);

    return () => {
      window.removeEventListener('virgo-answer-call', handleAnswer);
      window.removeEventListener('virgo-decline-call', handleDecline);
    };
  }, []);

  // Initialize on mount, never dispose (app-lifetime registration)
  useEffect(() => {
    // Small delay to let the app fully load and auth settle
    const timer = setTimeout(() => {
      initializeSip();
    }, 3000);

    return () => {
      clearTimeout(timer);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      // NOTE: We intentionally do NOT call ringCentralService.dispose() here.
      // The SIP registration should persist for the lifetime of the app.
    };
  }, [initializeSip]);

  return {
    sipState,
    isRegistered: sipState === 'registered',
    reinitialize: () => {
      initRef.current = false;
      initializeSip();
    }
  };
};

export default useGlobalSoftphone;
